import { Ref, ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';

export function useFontResizer(
  target: Ref<HTMLElement | null>,
  options?: ResizeObserverOptions,
  minFontSize = 12,
  maxFontSize = 32,
) {
  const fontSize = ref<string>('100%');
  const isTruncated = ref<boolean>(false);

  const OVERFLOW_PX = 1; // tolerance
  const PRECISION = 0.25; // px resolution for the search
  let inProgress = false;

  const fits = (el: HTMLElement) => {
    const overflowX = el.scrollWidth - el.clientWidth > OVERFLOW_PX;
    const overflowY = el.scrollHeight - el.clientHeight > OVERFLOW_PX;
    return !(overflowX || overflowY);
  };

  useResizeObserver(
    target,
    () => {
      const el = target.value;
      if (!el || inProgress) {
        return;
      }

      inProgress = true;
      try {
        // Always start from baseline (prevents "memory" and oscillation)
        let lo = minFontSize;
        let hi = maxFontSize;

        // Quick check at max: if it fits, use it and clear truncation
        el.style.fontSize = `${hi}px`;
        if (fits(el)) {
          fontSize.value = `${hi}px`;
          isTruncated.value = false;
          return;
        }

        // Quick check at min: if even min doesn't fit -> clamp + truncate
        el.style.fontSize = `${lo}px`;
        if (!fits(el)) {
          fontSize.value = `${lo}px`;
          isTruncated.value = true;
          return;
        }

        // Binary search for largest fitting size
        while (hi - lo > PRECISION) {
          const mid = (lo + hi) / 2;
          el.style.fontSize = `${mid}px`;

          if (fits(el)) {
            lo = mid;
          } else {
            hi = mid;
          }
        }

        // Snap to precision and apply
        const best = Math.floor(lo / PRECISION) * PRECISION;
        fontSize.value = `${best}px`;
        isTruncated.value = best <= minFontSize + PRECISION;
      } finally {
        inProgress = false;
      }
    },
    { box: 'border-box', ...options },
  );

  return { fontSize, isTruncated };
}
